import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sprout, ArrowLeft, LogOut, Loader2, Send, Trash2, MessagesSquare, UserRound, CheckCircle2, Bell, BellOff, KeyRound, UserX, ChevronDown, ImagePlus, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_IMAGES = 4;

const ForumImage = ({ path, alt, className, onClick, testId }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let active = true;
    let url = null;
    const token = localStorage.getItem("kga_forum_token");
    axios
      .get(`${API}/forum/images/${path}`, { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" })
      .then((r) => {
        if (active) {
          url = URL.createObjectURL(r.data);
          setSrc(url);
        }
      })
      .catch(() => {});
    return () => {
      active = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [path]);
  if (!src) return <div className={`animate-pulse bg-[#EFECE4] ${className}`} data-testid={testId} />;
  return <img src={src} alt={alt} onClick={onClick} className={className} data-testid={testId} />;
};
const FIELD =
  "w-full rounded-xl border border-[#D8D3C5] bg-white px-4 py-3 text-sm sm:text-base text-[#1A251E] outline-none transition-colors duration-300 focus:border-[#1A382B] placeholder:text-[#6F7E73]";
const LABEL = "block mb-1.5 font-jbm text-xs tracking-wider uppercase text-[#5A826B]";

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Etwas ist schiefgelaufen. Bitte erneut versuchen.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && e.msg) || "").filter(Boolean).join(" ");
  return String(detail);
};

const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleString("de-DE", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit", timeZone: "Europe/Berlin",
      }) + " Uhr"
    : "";

export default function Forum() {
  const [token, setToken] = useState(() => localStorage.getItem("kga_forum_token"));
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(!!localStorage.getItem("kga_forum_token"));
  const [mode, setMode] = useState("login");
  const [auth, setAuth] = useState({ first_name: "", last_name: "", parcel: "", email: "", password: "", notifications: false });
  const [error, setError] = useState("");
  const [registered, setRegistered] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [posts, setPosts] = useState([]);
  const [newPost, setNewPost] = useState({ title: "", text: "" });
  const [postImages, setPostImages] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const [posting, setPosting] = useState(false);
  const [openReplies, setOpenReplies] = useState(null);
  const [replyText, setReplyText] = useState({});
  const [profileOpen, setProfileOpen] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [deletePw, setDeletePw] = useState("");
  const [saving, setSaving] = useState(false);

  const headers = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        setUser(r.data);
        setChecking(false);
      })
      .catch(() => {
        localStorage.removeItem("kga_forum_token");
        setToken(null);
        setChecking(false);
      });
  }, []);

  const loadPosts = useCallback(async () => {
    const r = await axios.get(`${API}/forum/posts`, headers());
    setPosts(r.data);
  }, [headers]);

  useEffect(() => {
    if (user) loadPosts().catch(() => {});
  }, [user, loadPosts]);

  const setA = (k) => (e) => setAuth({ ...auth, [k]: e.target.value });

  const submitAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      if (mode === "register") {
        await axios.post(`${API}/auth/register`, auth);
        setRegistered(true);
        return;
      }
      const { data } = await axios.post(`${API}/auth/login`, { email: auth.email, password: auth.password });
      localStorage.setItem("kga_forum_token", data.token);
      setToken(data.token);
      const me = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${data.token}` } });
      setUser(me.data);
      toast.success(mode === "login" ? "Angemeldet" : "Willkommen im Forum", {
        description: `Schön, dass Sie da sind, ${me.data.name}!`,
      });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("kga_forum_token");
    setToken(null);
    setUser(null);
    setPosts([]);
  };

  const submitPost = async (e) => {
    e.preventDefault();
    setPosting(true);
    try {
      const images = [];
      for (const item of postImages) {
        const fd = new FormData();
        fd.append("file", item.file);
        const up = await axios.post(`${API}/forum/uploads`, fd, headers());
        images.push({ path: up.data.path, filename: up.data.filename });
      }
      await axios.post(`${API}/forum/posts`, { ...newPost, images }, headers());
      postImages.forEach((item) => URL.revokeObjectURL(item.preview));
      setPostImages([]);
      setNewPost({ title: "", text: "" });
      await loadPosts();
      toast.success("Beitrag veröffentlicht");
    } catch (err) {
      toast.error("Veröffentlichen fehlgeschlagen", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setPosting(false);
    }
  };

  const pickImages = (e) => {
    const files = Array.from(e.target.files || []).filter((f) => f.type.startsWith("image/"));
    e.target.value = "";
    if (!files.length) return;
    const room = MAX_IMAGES - postImages.length;
    if (files.length > room) {
      toast.error(`Maximal ${MAX_IMAGES} Bilder pro Beitrag`);
    }
    const next = files.slice(0, Math.max(room, 0)).map((file) => ({ file, preview: URL.createObjectURL(file) }));
    setPostImages([...postImages, ...next]);
  };

  const removeImage = (idx) => {
    URL.revokeObjectURL(postImages[idx].preview);
    setPostImages(postImages.filter((_, i) => i !== idx));
  };

  const submitReply = async (postId) => {
    const text = (replyText[postId] || "").trim();
    if (!text) return;
    try {
      await axios.post(`${API}/forum/posts/${postId}/replies`, { text }, headers());
      setReplyText({ ...replyText, [postId]: "" });
      await loadPosts();
    } catch (err) {
      toast.error("Antwort fehlgeschlagen", { description: formatApiErrorDetail(err.response?.data?.detail) });
    }
  };

  const deletePost = async (p) => {
    if (!window.confirm(`Beitrag „${p.title}“ wirklich löschen?`)) return;
    try {
      await axios.delete(`${API}/forum/posts/${p.id}`, headers());
      await loadPosts();
      toast.success("Beitrag gelöscht");
    } catch {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: auth.email });
      setForgotSent(true);
    } catch {
      setError("Anfrage fehlgeschlagen. Bitte versuchen Sie es erneut.");
    } finally {
      setLoading(false);
    }
  };

  const toggleNotifications = async () => {
    const next = !(user.notifications === true);
    try {
      await axios.post(`${API}/forum/notifications`, { enabled: next }, headers());
      setUser({ ...user, notifications: next });
      toast.success(next ? "Benachrichtigungen aktiviert" : "Benachrichtigungen abbestellt", {
        description: next
          ? "Sie erhalten Mails zu Beiträgen, die Sie erstellt oder kommentiert haben."
          : "Sie erhalten keine Forum-E-Mails mehr.",
      });
    } catch {
      toast.error("Änderung fehlgeschlagen");
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.new_password !== pw.confirm) {
      toast.error("Passwörter stimmen nicht überein");
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/auth/change-password`, {
        current_password: pw.current_password,
        new_password: pw.new_password,
      }, headers());
      localStorage.setItem("kga_forum_token", data.token);
      setToken(data.token);
      setPw({ current_password: "", new_password: "", confirm: "" });
      toast.success("Passwort geändert", { description: "Ihr neues Passwort ist ab sofort aktiv." });
    } catch (err) {
      toast.error("Ändern fehlgeschlagen", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setSaving(false);
    }
  };

  const deleteAccount = async (e) => {
    e.preventDefault();
    if (!window.confirm("Konto wirklich endgültig löschen? Ihre Beiträge bleiben anonymisiert als „Gelöschtes Mitglied“ erhalten. Das kann nicht rückgängig gemacht werden.")) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/auth/account`, { data: { password: deletePw }, ...headers() });
      localStorage.removeItem("kga_forum_token");
      setToken(null);
      setUser(null);
      setPosts([]);
      toast.success("Konto gelöscht", { description: "Ihre Daten wurden entfernt, Beiträge anonymisiert." });
    } catch (err) {
      toast.error("Löschen fehlgeschlagen", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F7F2] text-[#1A251E]" data-testid="forum-page">
      <header className="border-b border-[#D8D3C5] bg-[#F8F7F2]/90 backdrop-blur-md sticky top-0 z-40">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5" data-testid="forum-logo">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#1A382B] text-[#F8F7F2]">
              <Sprout size={18} strokeWidth={1.8} />
            </span>
            <span className="font-display text-xl font-bold text-[#1A382B]">Schönwald · Gärtner-Forum</span>
          </a>
          <div className="flex items-center gap-3">
            <a href="/" data-testid="forum-back-link" className="inline-flex items-center gap-1.5 text-sm font-medium text-[#47544A] hover:text-[#C85A32] transition-colors">
              <ArrowLeft size={15} /> Zur Website
            </a>
            {user && (
              <button onClick={logout} data-testid="forum-logout-button" className="inline-flex items-center gap-1.5 rounded-full border border-[#D8D3C5] px-4 py-2 text-sm font-semibold text-[#1A382B] hover:border-[#B93829] hover:text-[#B93829] transition-colors">
                <LogOut size={14} /> Abmelden
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 sm:px-6 py-12">
        {checking ? (
          <p className="text-center text-[#6F7E73]" data-testid="forum-loading">Sitzung wird geprüft …</p>
        ) : !user ? (
          <div className="mx-auto max-w-md">
            {forgotMode && !registered ? (
              <div className="rounded-2xl border border-[#D8D3C5] bg-white p-8 shadow-[0_18px_40px_rgba(26,56,43,0.08)]" data-testid="forgot-card">
                <h2 className="font-display text-2xl font-bold text-[#1A382B]">Passwort vergessen?</h2>
                {forgotSent ? (
                  <p className="mt-3 text-sm leading-relaxed text-[#47544A]" data-testid="forgot-success">
                    Falls ein Konto mit dieser E-Mail-Adresse existiert, haben wir Ihnen soeben einen Link
                    zum Zurücksetzen geschickt. Bitte prüfen Sie Ihr Postfach – der Link ist 60 Minuten gültig.
                  </p>
                ) : (
                  <>
                    <p className="mt-2 mb-5 text-sm leading-relaxed text-[#47544A]">
                      Geben Sie Ihre E-Mail-Adresse ein – wir schicken Ihnen einen Link zum Zurücksetzen.
                    </p>
                    {error && (
                      <p className="mb-4 rounded-xl bg-[#B93829]/10 px-4 py-3 text-sm font-medium text-[#B93829]">{error}</p>
                    )}
                    <label className={LABEL}>E-Mail *</label>
                    <input type="email" required className={FIELD} value={auth.email} onChange={setA("email")} data-testid="forgot-email-input" />
                    <button onClick={submitForgot} disabled={loading} data-testid="forgot-submit-button" className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#1A382B] px-7 py-3.5 text-sm font-semibold text-[#F8F7F2] transition-colors hover:bg-[#C85A32] disabled:opacity-60">
                      {loading && <Loader2 size={16} className="animate-spin" />}
                      Link zusenden
                    </button>
                  </>
                )}
                <button onClick={() => { setForgotMode(false); setForgotSent(false); setError(""); }} data-testid="forgot-back-button" className="mt-4 w-full text-center text-sm font-medium text-[#6F7E73] hover:text-[#C85A32] transition-colors">
                  Zurück zur Anmeldung
                </button>
              </div>
            ) : registered ? (
              <div className="rounded-2xl border border-[#5A826B] bg-white p-8 text-center shadow-[0_18px_40px_rgba(26,56,43,0.08)]" data-testid="forum-registered-card">
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#E2ECE5] text-[#1A382B]">
                  <CheckCircle2 size={26} />
                </span>
                <h2 className="mt-4 font-display text-2xl font-bold text-[#1A382B]">Registrierung eingegangen!</h2>
                <p className="mt-3 text-sm leading-relaxed text-[#47544A]">
                  Vielen Dank! Der Vorstand prüft Ihre Angaben und schaltet Ihren Zugang zum Forum
                  frei. Das kann einen Moment dauern – danach melden Sie sich hier einfach mit
                  E-Mail und Passwort an.
                </p>
                <button onClick={() => { setRegistered(false); setMode("login"); }} data-testid="forum-goto-login" className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#1A382B] px-6 py-3 text-sm font-semibold text-[#F8F7F2] transition-colors hover:bg-[#C85A32]">
                  Zur Anmeldung
                </button>
              </div>
            ) : (
            <form onSubmit={submitAuth} className="rounded-2xl border border-[#D8D3C5] bg-white p-8 shadow-[0_18px_40px_rgba(26,56,43,0.08)]" data-testid="forum-auth-card">
              <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1A382B]">Forum der Kleingärtner</h1>
              <p className="mt-2 mb-6 text-sm leading-relaxed text-[#47544A]">
                Dieser Bereich ist nur für angemeldete Kleingärtner sichtbar. Die Anmeldung erfolgt mit
                <strong> Klarnamen</strong> – Ihre Beiträge erscheinen unter Ihrem vollen Namen.
              </p>
              <div className="mb-6 grid grid-cols-2 rounded-full border border-[#D8D3C5] bg-[#F8F7F2] p-1">
                <button type="button" onClick={() => { setMode("login"); setError(""); }} data-testid="forum-login-tab" className={`rounded-full py-2 text-sm font-semibold transition-colors ${mode === "login" ? "bg-[#1A382B] text-[#F8F7F2]" : "text-[#47544A]"}`}>
                  Anmelden
                </button>
                <button type="button" onClick={() => { setMode("register"); setError(""); }} data-testid="forum-register-tab" className={`rounded-full py-2 text-sm font-semibold transition-colors ${mode === "register" ? "bg-[#1A382B] text-[#F8F7F2]" : "text-[#47544A]"}`}>
                  Registrieren
                </button>
              </div>
              {error && (
                <p className="mb-4 rounded-xl bg-[#B93829]/10 px-4 py-3 text-sm font-medium text-[#B93829]" data-testid="forum-auth-error">
                  {error}
                </p>
              )}
              {mode === "register" && (
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className={LABEL}>Vorname *</label>
                    <input required className={FIELD} value={auth.first_name} onChange={setA("first_name")} data-testid="forum-firstname-input" />
                  </div>
                  <div>
                    <label className={LABEL}>Nachname *</label>
                    <input required className={FIELD} value={auth.last_name} onChange={setA("last_name")} data-testid="forum-lastname-input" />
                  </div>
                  <div className="col-span-2">
                    <label className={LABEL}>Parzelle</label>
                    <input className={FIELD} value={auth.parcel} onChange={setA("parcel")} placeholder="z. B. 42" data-testid="forum-parcel-input" />
                  </div>
                </div>
              )}
              <label className={LABEL}>E-Mail *</label>
              <input type="email" required className={FIELD} value={auth.email} onChange={setA("email")} data-testid="forum-email-input" />
              <div className="mt-4">
                <label className={LABEL}>Passwort *{mode === "register" ? " (mind. 8 Zeichen)" : ""}</label>
                <input type="password" required minLength={mode === "register" ? 8 : undefined} className={FIELD} value={auth.password} onChange={setA("password")} data-testid="forum-password-input" />
              </div>
              {mode === "login" && (
                <button type="button" onClick={() => { setForgotMode(true); setError(""); }} data-testid="forgot-link" className="mt-2.5 text-sm font-medium text-[#6F7E73] hover:text-[#C85A32] transition-colors">
                  Passwort vergessen?
                </button>
              )}
              {mode === "register" && (
                <label className="mt-4 flex items-start gap-3 cursor-pointer rounded-xl border border-[#D8D3C5] bg-[#F8F7F2] px-4 py-3" data-testid="forum-notifications-optin">
                  <input
                    type="checkbox"
                    checked={auth.notifications}
                    onChange={(e) => setAuth({ ...auth, notifications: e.target.checked })}
                    data-testid="forum-notifications-checkbox"
                    className="mt-0.5 h-5 w-5 shrink-0 accent-[#1A382B]"
                  />
                  <span className="text-xs leading-relaxed text-[#47544A]">
                    <strong className="text-[#1A382B]">E-Mail-Benachrichtigungen (freiwillig):</strong> Ich möchte per
                    E-Mail informiert werden, wenn es in Beiträgen, die ich erstellt oder kommentiert habe,
                    neue Antworten gibt. Widerruf jederzeit per Link in jeder E-Mail oder im Forum möglich.
                  </span>
                </label>
              )}
              <button type="submit" disabled={loading} data-testid="forum-auth-submit" className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#1A382B] px-7 py-3.5 text-sm font-semibold text-[#F8F7F2] transition-all duration-300 hover:bg-[#C85A32] disabled:opacity-60">
                {loading && <Loader2 size={16} className="animate-spin" />}
                {mode === "login" ? "Anmelden" : "Mit Klarnamen registrieren"}
              </button>
            </form>
            )}
          </div>
        ) : (
          <div data-testid="forum-content">
            <div className="mb-8 flex items-center gap-3 rounded-2xl border border-[#D8D3C5] bg-white px-5 py-4">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#E2ECE5] text-[#1A382B]">
                <UserRound size={18} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-jbm text-[10px] tracking-[0.25em] uppercase text-[#5A826B]">Angemeldet als</p>
                <p className="font-semibold text-[#1A382B]" data-testid="forum-username">{user.name}</p>
              </div>
              <button
                onClick={toggleNotifications}
                data-testid="forum-notifications-toggle"
                title={user.notifications ? "E-Mail-Benachrichtigungen abbestellen" : "E-Mail-Benachrichtigungen aktivieren"}
                className={`inline-flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-xs font-semibold transition-colors ${
                  user.notifications
                    ? "border-[#5A826B] bg-[#E2ECE5] text-[#1A382B]"
                    : "border-[#D8D3C5] text-[#6F7E73] hover:border-[#5A826B]"
                }`}
              >
                {user.notifications ? <Bell size={14} /> : <BellOff size={14} />}
                <span className="hidden sm:inline">{user.notifications ? "Mails an" : "Mails aus"}</span>
              </button>
            </div>

            <div className="mb-8 rounded-2xl border border-[#D8D3C5] bg-white" data-testid="forum-profile">
              <button
                onClick={() => setProfileOpen(!profileOpen)}
                data-testid="forum-profile-toggle"
                className="flex w-full items-center justify-between px-5 py-4"
              >
                <span className="inline-flex items-center gap-2.5 font-display text-lg font-semibold text-[#1A382B]">
                  <KeyRound size={17} className="text-[#5A826B]" /> Mein Konto
                </span>
                <ChevronDown size={17} className={`text-[#6F7E73] transition-transform duration-300 ${profileOpen ? "rotate-180" : ""}`} />
              </button>
              {profileOpen && (
                <div className="border-t border-[#EFECE4] px-5 py-5 space-y-8">
                  <form onSubmit={changePassword} data-testid="forum-password-form">
                    <p className="font-jbm text-xs tracking-wider uppercase text-[#5A826B] mb-3">Passwort ändern</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <input type="password" required className={FIELD} value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} placeholder="Bisheriges Passwort" data-testid="pw-current-input" />
                      <input type="password" required minLength={8} className={FIELD} value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} placeholder="Neues Passwort (mind. 8)" data-testid="pw-new-input" />
                      <input type="password" required className={FIELD} value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} placeholder="Neues Passwort wiederholen" data-testid="pw-confirm-input" />
                    </div>
                    <button type="submit" disabled={saving} data-testid="pw-submit-button" className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#1A382B] px-5 py-2.5 text-sm font-semibold text-[#F8F7F2] transition-colors hover:bg-[#C85A32] disabled:opacity-60">
                      {saving && <Loader2 size={14} className="animate-spin" />} Passwort speichern
                    </button>
                  </form>
                  <form onSubmit={deleteAccount} className="rounded-xl border border-[#B93829]/25 bg-[#B93829]/5 p-4" data-testid="forum-delete-form">
                    <p className="font-jbm text-xs tracking-wider uppercase text-[#B93829] mb-2">Konto löschen (DSGVO Löschrecht)</p>
                    <p className="text-xs leading-relaxed text-[#47544A] mb-3">
                      Löscht Ihr Konto und alle persönlichen Daten unwiderruflich. Ihre Beiträge bleiben
                      anonymisiert als „Gelöschtes Mitglied“ erhalten.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <input type="password" required className={FIELD} value={deletePw} onChange={(e) => setDeletePw(e.target.value)} placeholder="Zur Bestätigung: Ihr Passwort" data-testid="delete-password-input" />
                      <button type="submit" disabled={saving} data-testid="delete-account-button" className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-[#B93829] px-5 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60">
                        <UserX size={14} /> Endgültig löschen
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>

            <form onSubmit={submitPost} className="mb-8 rounded-2xl border border-[#D8D3C5] bg-white p-6 shadow-[0_18px_40px_rgba(26,56,43,0.08)]" data-testid="forum-newpost-form">
              <h2 className="font-display text-xl sm:text-2xl font-semibold text-[#1A382B]">Neuer Beitrag</h2>
              <div className="mt-4 space-y-4">
                <input required className={FIELD} value={newPost.title} onChange={(e) => setNewPost({ ...newPost, title: e.target.value })} placeholder="Überschrift" data-testid="forum-post-title-input" />
                <textarea required rows={4} className={FIELD} value={newPost.text} onChange={(e) => setNewPost({ ...newPost, text: e.target.value })} placeholder="Worum geht es?" data-testid="forum-post-text-input" />
                <div>
                  <input
                    id="forum-image-upload"
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    multiple
                    className="hidden"
                    onChange={pickImages}
                    data-testid="forum-post-image-input"
                  />
                  <label
                    htmlFor="forum-image-upload"
                    data-testid="forum-post-image-button"
                    className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-[#D8D3C5] px-5 py-2.5 text-sm font-semibold text-[#1A382B] transition-colors hover:border-[#5A826B] hover:text-[#5A826B]"
                  >
                    <ImagePlus size={16} /> Fotos anhängen ({postImages.length}/{MAX_IMAGES})
                  </label>
                  {postImages.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-3" data-testid="forum-image-previews">
                      {postImages.map((item, i) => (
                        <div key={item.preview} className="relative h-20 w-20 overflow-hidden rounded-xl border border-[#D8D3C5]">
                          <img src={item.preview} alt={item.file.name} className="h-full w-full object-cover" data-testid={`forum-image-preview-${i}`} />
                          <button
                            type="button"
                            onClick={() => removeImage(i)}
                            aria-label="Bild entfernen"
                            data-testid={`forum-image-remove-${i}`}
                            className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-[#1A251E]/70 text-white transition-colors hover:bg-[#B93829]"
                          >
                            <X size={13} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="mt-2 text-xs text-[#6F7E73]">Bis zu {MAX_IMAGES} Fotos (JPG, PNG, WebP, GIF – je max. 5 MB), z. B. für Tauschangebote aus dem Garten.</p>
                </div>
              </div>
              <button type="submit" disabled={posting} data-testid="forum-post-submit" className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#1A382B] px-6 py-3 text-sm font-semibold text-[#F8F7F2] transition-all duration-300 hover:bg-[#C85A32] disabled:opacity-60">
                {posting ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                Veröffentlichen
              </button>
            </form>

            {posts.length === 0 && (
              <p className="rounded-2xl border border-[#D8D3C5] bg-white p-6 text-sm text-[#47544A]" data-testid="forum-empty">
                Noch keine Beiträge – schreiben Sie den ersten!
              </p>
            )}

            <div className="space-y-4" data-testid="forum-posts">
              {posts.map((p, i) => (
                <article key={p.id} className="rounded-2xl border border-[#D8D3C5] bg-white p-6" data-testid={`forum-post-${i}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="font-display text-xl font-semibold text-[#1A382B]">{p.title}</h3>
                      <p className="mt-1 font-jbm text-xs tracking-wider uppercase text-[#5A826B]">
                        {p.author_name} · {fmtDate(p.created_at)}
                      </p>
                    </div>
                    {user.role === "admin" && (
                      <button onClick={() => deletePost(p)} data-testid={`forum-delete-${i}`} aria-label="Beitrag löschen" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#D8D3C5] text-[#B93829] hover:border-[#B93829] transition-colors">
                        <Trash2 size={15} />
                      </button>
                    )}
                  </div>
                  <p className="mt-3 text-sm sm:text-base leading-relaxed text-[#2A332E] whitespace-pre-wrap">{p.text}</p>
                  {p.images?.length > 0 && (
                    <div className={`mt-4 grid gap-3 ${p.images.length === 1 ? "grid-cols-1 max-w-sm" : "grid-cols-2"}`} data-testid={`forum-post-images-${i}`}>
                      {p.images.map((img, j) => (
                        <ForumImage
                          key={img.path}
                          path={img.path}
                          alt={img.filename || "Foto aus dem Garten"}
                          testId={`forum-post-image-${i}-${j}`}
                          onClick={() => setLightbox(img.path)}
                          className="h-40 w-full cursor-zoom-in rounded-xl border border-[#EFECE4] object-cover transition-transform duration-300 hover:scale-[1.02]"
                        />
                      ))}
                    </div>
                  )}
                  <button
                    onClick={() => setOpenReplies(openReplies === p.id ? null : p.id)}
                    data-testid={`forum-replies-toggle-${i}`}
                    className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#C85A32] hover:text-[#1A382B] transition-colors"
                  >
                    <MessagesSquare size={15} />
                    {p.replies.length === 0 ? "Antworten" : `${p.replies.length} ${p.replies.length === 1 ? "Antwort" : "Antworten"}`}
                  </button>
                  {openReplies === p.id && (
                    <div className="mt-4 space-y-3 border-t border-[#EFECE4] pt-4" data-testid={`forum-replies-${i}`}>
                      {p.replies.map((r) => (
                        <div key={r.id} className="rounded-xl bg-[#F8F7F2] border border-[#EFECE4] px-4 py-3">
                          <p className="font-jbm text-[10px] tracking-wider uppercase text-[#5A826B]">
                            {r.author_name} · {fmtDate(r.created_at)}
                          </p>
                          <p className="mt-1 text-sm leading-relaxed text-[#2A332E] whitespace-pre-wrap">{r.text}</p>
                        </div>
                      ))}
                      <div className="flex gap-2">
                        <input
                          className={FIELD}
                          value={replyText[p.id] || ""}
                          onChange={(e) => setReplyText({ ...replyText, [p.id]: e.target.value })}
                          placeholder="Ihre Antwort …"
                          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), submitReply(p.id))}
                          data-testid={`forum-reply-input-${i}`}
                        />
                        <button onClick={() => submitReply(p.id)} data-testid={`forum-reply-submit-${i}`} aria-label="Antwort senden" className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#1A382B] text-[#F8F7F2] transition-colors hover:bg-[#C85A32]">
                          <Send size={16} />
                        </button>
                      </div>
                    </div>
                  )}
                </article>
              ))}
            </div>
          </div>
        )}
      </main>

      {lightbox && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-[#1A251E]/85 p-4 backdrop-blur-sm"
          onClick={() => setLightbox(null)}
          data-testid="forum-lightbox"
        >
          <button
            onClick={() => setLightbox(null)}
            aria-label="Schließen"
            data-testid="forum-lightbox-close"
            className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full bg-[#F8F7F2]/15 text-[#F8F7F2] transition-colors hover:bg-[#C85A32]"
          >
            <X size={20} />
          </button>
          <ForumImage
            path={lightbox}
            alt="Foto in Großansicht"
            testId="forum-lightbox-image"
            className="max-h-[85vh] max-w-full rounded-2xl object-contain shadow-2xl"
          />
        </div>
      )}
    </div>
  );
}
